import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "@src/common/ReactSelect";
import InputField from "@src/common/InputField";
import { useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { useLanguage } from "@src/context/LanguageContext";
import { useDispatch } from "react-redux";
import { setQuotationAddonsData, setQuotationData } from "@src/store/slices/insurance";
import { LEAD_DETAIL_AMOUNT_FIELDS, LEAD_TYPE_CRM, VEHICLE_CONDITION } from "@src/config/id/constant";
import * as HELPER from "../../../config/helper";
import { COUNTRY_CODE } from "@src/config/constant";
import { ExpiryEffectivityDate } from "../../../../src/common/DatePicker";

const YES = 1;
const MAX_TENURE = 5;
const INSURANCE_TYPE_SHORT_LABEL: Record<number, string> = { 1: "Comprehensive", 2: "TLO" };

/* ----- field grouping for the calculator sections ----- */
const COVERAGE_KEYS = ["vehicle_condition_id", "usage_type", "is_accessories", "acc_count", "accessories_description"];
const POLICY_KEYS = ["insurer_name", "insurance_type_id", "insurance_starting_date", "__tenure__", "rule_engine_id", "copy_of_insurance", "insurance_exists", "existing_insurer_name", "other_insurer_name", "insurance_expire_date", "bstk_insurer_date"];
const PREMIUM_KEYS = ["car_fmv", "acc_price", "total_fmv", "basic_premium_amount", "total_addons_premium"];
// CRM leads: customer cashback is cash_customer_per / cash_customer (insurance_lead_details), no Effective Cashback field.
// Non-CRM leads: cashback is offset_points / offset_points_amt / offset_points_amt_after_tax.
const COMMERCIAL_KEYS_CRM = ["gwp_amount", "admin_fee", "customer_discount", "customer_discount_per", "agent_comission_amount", "agent_commision", "cash_customer", "cash_customer_per", "agent_point_amount", "point_commision", "final_premium"];
const COMMERCIAL_KEYS_NON_CRM = ["gwp_amount", "admin_fee", "customer_discount", "customer_discount_per", "agent_comission_amount", "agent_commision", "offset_points_amt", "offset_points", "offset_points_amt_after_tax", "agent_point_amount", "point_commision", "final_premium"];
// kept in the field list (the share-quote popup reads them from the store) but shown only on the Vehicle Details card
const VEHICLE_ONLY_KEYS = ["make", "model", "version", "make_year", "plate_area", "plate_number_region"];
const ACCESSORY_GATED_KEYS = ["acc_count", "accessories_description", "acc_price"];
const PERCENT_KEYS = ["customer_discount_per", "cash_customer_per", "offset_points"];
const CASHBACK_KEYS = ["cash_customer_per", "cash_customer", "offset_points", "offset_points_amt", "offset_points_amt_after_tax"];

const toNumber = (value: any) => {
    const num = Number(typeof value === "string" ? value.replace(/[^\d.-]/g, "") : value);
    return isNaN(num) ? 0 : num;
};
// Mirrors backend global.POLICY_HOLDER / *_OFFSET_POINTS_TAX_PERCENT (tax on the customer cashback)
const POLICY_HOLDER_PERSONAL = 1;
const POLICY_HOLDER_COMPANY = 2;
const PERSONAL_OFFSET_POINTS_TAX_PERCENT = 2.5;
const COMPANY_OFFSET_POINTS_TAX_PERCENT = 2;
const hasValue = (value: any) => value !== null && value !== undefined && value !== "";

// digits with at most one decimal point and 4 decimal places
const sanitizePercent = (value: any) => {
    let v = String(value ?? "").replace(/[^\d.]/g, "");
    const dot = v.indexOf(".");
    if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
    const [intPart, decPart] = v.split(".");
    return decPart !== undefined ? `${intPart}.${decPart.slice(0, 4)}` : intPart;
};

const formatMultiYearInsurance = (types: number[]) => {
    const ordinal = (n: number) => {
        const j = n % 10, k = n % 100;
        if (j === 1 && k !== 11) return `${n}st`;
        if (j === 2 && k !== 12) return `${n}nd`;
        if (j === 3 && k !== 13) return `${n}rd`;
        return `${n}th`;
    };
    const grouped: Record<number, number[]> = {};
    types.forEach((type, idx) => { (grouped[type] = grouped[type] || []).push(idx + 1); });
    return Object.entries(grouped).map(([type, years]) => {
        const name = INSURANCE_TYPE_SHORT_LABEL[+type] || "";
        return years.length > 1 ? `${name} (${ordinal(years[0])} to ${ordinal(years[years.length - 1])} Year)` : `${name} (${ordinal(years[0])} Year)`;
    }).join(", ");
};

export default function QuotesDetailID({
    formType,
    addonCount,
    showHideAddons,
    toggleswitch,
    calculateInsurance,
    previewInsurance,
    quotationDetails,
    showApproveBtn,
    saveInsurance,
    totalAddonPremiumAmt,
    tenure,
    savedTenure,
    onTenureChange,
    yearTypes,
    yearAddonSelections,
    needsRecalc
}: any) {
    const [isCalculateView, setIsCalculateView] = useState(false);
    const [fields, setFields] = useState<any[]>([]);
    const [refresh, setRefresh] = useState(false);
    const [activeYear, setActiveYear] = useState<number>(1);
    const lastCashbackKey = useRef<string | null>(null);
    // which of "Discount to Customer in Rp / %" the user typed last - the other one is derived from it
    const lastDiscountKey = useRef<string | null>(null);
    // live preview of the commercial terms while typing (same calculator API as the Calculate button)
    const previewTimer = useRef<any>(null);
    const previewRequestId = useRef(0);
    const [isPreviewing, setIsPreviewing] = useState(false);
    // non-CRM cashback is paid out of agent points: max cashback % = agent points before any cashback
    const basePointsRef = useRef<number | null>(null);
    useEffect(() => () => clearTimeout(previewTimer.current), []);

    const commonState: any = useSelector((state: any) => state.common);
    const insuranceState: any = useSelector((state: any) => state.insurance);
    const dispatch:any = useDispatch();
    const leadDetail = insuranceState?.leadDetail || {};
    const isCrmLead = leadDetail?.lead_type === LEAD_TYPE_CRM;
    const currentTenure = Math.max(1, Number(tenure) || Number(leadDetail?.tenure) || leadDetail?.lead_tenure_data?.length || 1);
    const isMultiYear = currentTenure > 1;
    const isEdit = formType === "EDIT";

    // Effect to update quoteData in Redux when fields change
    useEffect(() => {
        if (fields.length > 0) {
            const clonedFields = fields.map(field => ({
                ...field,
                options: Array.isArray(field.options) ? [...field.options] : field.options,
                hidden: isFieldDisabled(field.key), // Store only the boolean value
            }));
            dispatch(setQuotationData(clonedFields));
        }// eslint-disable-next-line
    }, [fields, dispatch]);

    useEffect(() => {
        if (activeYear > currentTenure) setActiveYear(1);
    }, [currentTenure, activeYear]);

    // eslint-disable-next-line
    const [DISCOUNTMAX, SETDISCOUNTMAX] = useState(commonState?.common?.CUSTOMER_DISCOUNT_MAX)
    const {translations} = useLanguage();
    const t = (key: string, fallback?: string) => translations.Insurance?.[key] || fallback || translations.commonMsg?.loading;

    const disabledFields = [
        "insurer_name",
        "insurance_type_id",
        "vehicle_condition_id",
        "usage_type",
        "insurance_starting_date",
        "make",
        "model",
        "version",
        "make_year",
        "plate_area",
        "plate_number_region",
        "total_fmv",
        'admin_fee',
        "net_premium",
        "agent_point_amount",
        "agent_comission_amount",
        "total_addons_premium",
        "gwp_amount",
        "rule_engine_id",
        "final_premium",
        "basic_premium_amount",
        "agent_commision",
        "point_commision",
        "bstk_insurer_date",
    ];

    const dynamicFields = [
        "car_fmv",
        "acc_price",
        "total_fmv",
        "admin_fee",
        "total_premium_amount",
        "agent_comission_amount",
        "agent_point_amount",
        "is_accessories",
        "accessories_description",
        "customer_discount",
        'net_premium',
        'total_addons_premium',
        'final_premium',
        "customer_discount_per",
        "copy_of_insurance",
        "insurance_exists",
        "expiration_date",
        "acc_count",
        "existing_insurer_name",
        "insurance_expire_date",
        "rule_engine_id",
        "other_insurer_name",
        "gwp_amount",
        "basic_premium_amount",
        "agent_commision",
        "point_commision",
        "cash_customer",
        "cash_customer_per",
        "offset_points",
        "offset_points_amt",
        "offset_points_amt_after_tax"
    ];

    // Percentage fields: the DB keeps up to 6 decimals, the UI shows / accepts max 4 (GACOM-9845)
    const PERCENT_FIELDS = ["customer_discount_per", "cash_customer_per", "agent_commision", "point_commision", "offset_points"];
    const PERCENT_INPUT_DECIMALS = 4;
    // 24.999981 -> 25, 12.5000 -> 12.5; non-numeric / empty values are returned unchanged
    const formatPercentValue = (value: any) => {
        if (value === null || value === undefined || value === "") return value;
        const num = Number(value);
        return Number.isFinite(num) ? +num.toFixed(PERCENT_INPUT_DECIMALS) : value;
    };

    const keyMapping: Record<string, string> = {
        insurance_type_id: "insurance_type",
        vehicle_condition_id: "vehicle_condition",
        usage_type: "vehicle_usage",
        rule_engine_id: "rule_engine"
    };

    const getFieldItem = (key: any) => {
        return fields?.find((e: any) => e.key === key) || {}
    }

    // Get field options from store
    const getFieldItemOptions = (fieldItem: any) => {        
        let { key } = fieldItem

        if (fieldItem?.options?.length) return fieldItem.options

        let options = (key === "is_accessories" || key === "insurance_exists" || key === "copy_of_insurance")
            ? commonState?.common?.YesAndNo || []
            : commonState?.common?.[keyMapping[key] || key] || [];

        if(key === 'existing_insurer_name'){
            let insurerList = commonState?.common?.insurer_list || [];
            options = insurerList;
        }
        fieldItem['options'] = options
        return options
    };

    const isAccessoriesYes = () => {
        const accField = getFieldItem('is_accessories');
        const raw = hasValue(accField?.value) ? accField.value : ((showApproveBtn ? quotationDetails?.is_accessories : null) ?? leadDetail?.is_accessories);
        return +(typeof raw === 'object' ? raw?.value : raw) === YES;
    };

    const getPeriodEnd = (startDate: any) => {
        const tenureChanged = savedTenure && +savedTenure !== +currentTenure;
        if (showApproveBtn && quotationDetails?.insurance_end_date) return quotationDetails.insurance_end_date;
        if (!tenureChanged) {
            const lastYear = [...(leadDetail?.lead_tenure_data || [])].sort((a: any, b: any) => +b.tenure - +a.tenure)?.[0];
            const savedEnd = leadDetail?.insurance_ending_date || lastYear?.insurance_end_date;
            if (savedEnd) return savedEnd;
        }
        // CRM-originated leads may not carry an end date: derive it from start + tenure
        return startDate ? moment(startDate).add(currentTenure, 'year') : null;
    };

    // Get dynamic field values
    const getFieldItemValue = (fieldItem: any) => {
        let { key, type, value } = fieldItem

        // ---- always recomputed: they depend on tenure, not only on the saved lead ----
        if (key === "insurance_starting_date") {
            const startDate = (showApproveBtn && quotationDetails?.insurance_start_date) || leadDetail?.insurance_starting_date;
            const endDate = getPeriodEnd(startDate);
            const formattedStartDate = startDate ? moment(startDate).format("DD MMM YYYY") : "";
            const formattedExpireDate = endDate ? moment(endDate).format("DD MMM YYYY") : "";
            const period = formattedStartDate && formattedExpireDate ? `${formattedStartDate} - ${formattedExpireDate}` : formattedStartDate;
            fieldItem["value"] = period;
            return period;
        }
        if (key === "insurance_type_id") {
            const tenureChanged = savedTenure && +savedTenure !== +currentTenure;
            const finalValue = (showApproveBtn && quotationDetails?.insurance_all_type)
                || ((tenureChanged && yearTypes?.length) ? formatMultiYearInsurance(yearTypes) : '')
                || leadDetail?.insurance_all_type
                || quotationDetails?.insurance_all_type
                || commonState?.common?.insurance_type?.find((e: any) => e.value === (typeof value === "object" ? value?.value : value))?.label
                || "";
            fieldItem["value"] = finalValue;
            return finalValue;
        }
        if (fieldItem.cleared) return type === 'dd' ? null : '';

        if (value || value === 0) return value

        value = (showApproveBtn && dynamicFields.includes(key))
            ? quotationDetails?.[key]
            : ((dynamicFields.includes(key) && fieldItem.hasOwnProperty('value')) ? fieldItem?.['value'] : leadDetail?.[key]);

        let finalValue = null

        finalValue = type === "dd"
            ? getFieldItemOptions(fieldItem)?.find((e: any) => e.value === (typeof value === 'object' ? value?.value : value)) || ""
            : value;

        if (["make", "model", "version"].includes(key)) {
            if(key==='model') key ='parent_model'
            finalValue = { value: leadDetail?.[`${key}_name`], label: leadDetail?.[`${key}_name`] };
        }

        // stored DECIMALs come back as "25.000000": show at most 4 decimals
        if ([...PERCENT_KEYS, 'agent_commision', 'point_commision'].includes(key) && hasValue(finalValue) && !isNaN(Number(finalValue))) {
            finalValue = String(Number(Number(finalValue).toFixed(4)));
        }

        if(key === 'insurance_expire_date'){
            finalValue = value ? moment(value).toDate() : null;
        }

        if (key === 'plate_number_region') {
            const plateArea = fields?.find((e: any) => e.key === 'plate_area')?.value?.value
            finalValue = commonState?.common?.plate_area?.find((v: any) => Number(+plateArea === +v.value))?.region || ''
        }

        if(COUNTRY_CODE === 'id'){
            if (key === "total_addons_premium" && totalAddonPremiumAmt) {
                if (totalAddonPremiumAmt?.includes('Rp')) {
                    totalAddonPremiumAmt = HELPER.removeCurrencyFormatting(totalAddonPremiumAmt);
                }
                finalValue = totalAddonPremiumAmt;
            }
        }

        if (COUNTRY_CODE === 'id') {
            // Derived, not trusted from leadDetail.final_premium - the legacy quotation engine that fills it in has bugs.
            // cashback: CRM -> cash_customer, non-CRM -> offset_points_amt
            if (key === "final_premium" && showApproveBtn && hasValue(quotationDetails?.final_premium)) {
                // calculated (not yet saved) quotation: show its total, not the saved lead's
                finalValue = Math.round(Number(quotationDetails.final_premium) || 0);
            } else if (key === "final_premium") {
                const gwp = Number(insuranceState?.leadDetail?.gwp_amount) || 0;
                const adminFeeVal = Number(insuranceState?.leadDetail?.admin_fee) || 0;
                const discountVal = Number(insuranceState?.leadDetail?.customer_discount) || 0;
                // non-CRM: the stored discount already includes the effective cashback; CRM keeps cashback separate
                const cashbackVal = isCrmLead ? Number(insuranceState?.leadDetail?.cash_customer) || 0 : 0;
                finalValue = Math.round(gwp + adminFeeVal - discountVal - cashbackVal);
            }
        }

        fieldItem['value'] = finalValue
        
        return finalValue
    };

    const CASHBACK_GATED_KEYS = ["offset_points", "offset_points_amt", "offset_points_amt_after_tax", "cash_customer_per", "cash_customer"];
    const CASHBACK_UNLOCK_DISCOUNT_PER = Number(DISCOUNTMAX) || 25;
    const getCustomerDiscountPer = () => {
        const per = Number(getFieldItem('customer_discount_per')?.value) || 0;
        if (per > 0) return per;
        const discountAmt = toNumber(getFieldItem('customer_discount')?.value);
        const gwp = toNumber(getFieldItem('gwp_amount')?.value);
        return gwp > 0 ? (discountAmt / gwp) * 100 : 0;
    };
    const isCashbackUnlocked = () => getCustomerDiscountPer() >= CASHBACK_UNLOCK_DISCOUNT_PER - 0.01;

    const isFieldDisabled = (key: string) => {
        if (isCalculateView) return true;
        if (formType !== "EDIT") return true;
        if (disabledFields.includes(key)) return true;
        if (CASHBACK_GATED_KEYS.includes(key) && !isCashbackUnlocked()) return true;
        // No Of Accessories / Items / Price of Accessories are only editable when Accessories = Yes
        if (ACCESSORY_GATED_KEYS.includes(key) && !isAccessoriesYes()) return true;
        return false;
    };

    const shouldShowField = (key: string) => !VEHICLE_ONLY_KEYS.includes(key);

    const clearAccessoryFields = () => {
        for (const key of ACCESSORY_GATED_KEYS) {
            const item = getFieldItem(key);
            if (!item?.key) continue;
            if (key === 'acc_price') {
                item.value = 0;
            } else {
                item.value = null;
                item.cleared = true;
            }
        }
        const totalFMV = getFieldItem('total_fmv');
        if (totalFMV?.key) totalFMV.value = +(getFieldItem('car_fmv')?.value || 0);
    };

    const handleChange = (fieldItem: any, value: any) => {        
        let { key, type } = fieldItem
        let fieldValue = value?.target?.value || value?.value
        delete fieldItem.cleared;

        // drop leading zeros while typing: "012" -> "12", "00.5" -> "0.5" (a lone "0" or "0." is kept)
        if (PERCENT_FIELDS.includes(key) && typeof fieldValue === "string") {
            fieldValue = fieldValue.replace(/^0+(?=\d)/, "");
        }

        if (PERCENT_FIELDS.includes(key) && fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
            // allow digits with max 4 decimals only (e.g. 12.3456); ignore the keystroke otherwise
            if (!/^\d*\.?\d*$/.test(String(fieldValue)) || !HELPER.isValidDecimal(fieldValue, PERCENT_INPUT_DECIMALS)) {
                toast.error(translations?.commonMsg?.MAX_4D || "Max 4 decimals allowed");
                setRefresh(!refresh); // re-render so InputField drops the rejected keystroke
                return;
            }
        }
        const typedPercentValue = fieldValue;

        
        if(LEAD_DETAIL_AMOUNT_FIELDS?.includes(key) && COUNTRY_CODE === 'id') {
            fieldValue = HELPER.removeCurrencyFormattingId(fieldValue);
            if(['customer_discount', 'customer_discount_per','acc_price'].includes(key)){
                for(let row of fields){
                    if(LEAD_DETAIL_AMOUNT_FIELDS?.includes(row.key)){
                        row.value = HELPER.removeCurrencyFormattingId(row.value);
                    }
                }
            }
        }

        if (CASHBACK_KEYS.includes(key) && !isCrmLead && hasValue(fieldValue)) {
            const maxPer = getMaxCashbackPer();
            const requestedPer = getCashbackPercent(key, fieldValue);
            if (requestedPer > maxPer + 0.0001) {
                const gwp = toNumber(getFieldItemValue(getFieldItem('gwp_amount')));
                const maxAmt = Math.floor((gwp * maxPer) / 100);
                const maxLabel = key === 'offset_points' ? `${formatPercentValue(maxPer)}%`
                    : HELPER.addCurrencyFormatting(key === 'offset_points_amt' ? maxAmt : Math.floor(maxAmt - (maxAmt * getCashbackTaxPercent()) / 100));
                toast.error(`Customer Cashback cannot be more than ${maxLabel} (Maximum Usable Points: ${formatPercentValue(maxPer)}%)`);
                setRefresh(!refresh);
                return;
            }
        }

        if (CASHBACK_KEYS.includes(key)) lastCashbackKey.current = key;

        if (['customer_discount', 'customer_discount_per'].includes(key)) {
            // keep Rp and % in sync (both are on the whole-policy GWP), like the Additional Discount popup
            const gwpField = getFieldItem('gwp_amount');
            const gwp = toNumber(gwpField?.key ? getFieldItemValue(gwpField) : leadDetail?.gwp_amount);
            let discountField = null
            let totalDiscountPercent = 0
            let newValue: any = 0

            if (key === 'customer_discount') {
                discountField = getFieldItem('customer_discount_per')
                const amount = toNumber(fieldValue)
                totalDiscountPercent = gwp > 0 ? +((amount / gwp) * 100).toFixed(PERCENT_INPUT_DECIMALS) : 0
                newValue = totalDiscountPercent
            } else {
                discountField = getFieldItem('customer_discount')
                totalDiscountPercent = Number(fieldValue) || 0
                newValue = gwp > 0 ? Math.round((gwp * totalDiscountPercent) / 100) : 0
            }

            if (totalDiscountPercent > DISCOUNTMAX) {
                toast.error(`Discount should be less than ${DISCOUNTMAX}% of Overall Car Price`)
                fieldValue = fieldItem['value']
            } else if (discountField?.key) {
                discountField['value'] = newValue
                discountField['edited'] = false
                lastDiscountKey.current = key
            }
        }

        if (['acc_price', 'car_fmv'].includes(key)) {
            const findPriceField = key === 'acc_price' ? getFieldItem('car_fmv') : getFieldItem('acc_price')
            const totalFMV = fields?.find((el: any) => el.key === 'total_fmv')
            totalFMV['value'] = (+findPriceField?.value || 0) + (+fieldValue || 0)
        }

        // show the typed text while editing; if it was rejected (e.g. over the max) keep the 4-dp display of the kept value
        if (PERCENT_FIELDS.includes(key)) fieldItem["edited"] = fieldValue === typedPercentValue;

        fieldItem['value'] = type === 'dd' ? fieldItem?.options?.find((e: any) => e.value === fieldValue) : fieldValue

        if(type === 'date'){
            fieldItem['value'] = moment(value).toISOString();
        }

        // Accessories Yes -> No clears the dependent fields instead of leaving stale values
        if (key === 'is_accessories' && +fieldValue !== YES) {
            clearAccessoryFields();
        }

        const updatedFields = fields.map((f: any) =>
            f.key === key
                ? {
                    ...f,
                    value:
                        type === 'dd'
                            ? f.options?.find((opt: any) => opt.value === fieldValue)
                            : type === 'date'
                                ? moment(value).toISOString()
                                : fieldValue,
                }
                : f
        );

        if (key === "insurance_exists") {
            let newFields = [...updatedFields];

            newFields = newFields.filter(
                (f) => !["existing_insurer_name", "other_insurer_name", "insurance_expire_date"].includes(f.key)
            );

            if (fieldValue == 1) {
                newFields.push({
                    key: "existing_insurer_name",
                    type: "dd",
                    label: t('Insurance Policy Insurer'),
                    placeholder: t('Insurance Policy Insurer'),
                });
                
                const existingInsurerField = fields?.find((f: any) => f.key === 'existing_insurer_name');
                const existingInsurerValue = existingInsurerField?.value?.value ?? leadDetail?.existing_insurer_name;                    
                
                if (existingInsurerValue == 0) {
                    newFields.push({
                        key: "other_insurer_name",
                        type: "text",
                        label: t('Other'),
                        placeholder: t('Other'),
                    });
                }

                newFields.push({
                    key: "insurance_expire_date",
                    type: "date",
                    label: t('Insurance Policy Expiration Date'),
                    placeholder: t('Insurance Policy Expiration Date'),
                });
            }

            setFields(newFields.map((field) => ({ ...field, classname: "form-filed col-md-6", show: shouldShowField(field.key) })));
            return;
        }

        if (key === "existing_insurer_name") {
            let newFields = [...updatedFields];

            newFields = newFields.filter((f) => f.key !== "other_insurer_name");

            if (fieldValue == 0) {
                newFields.push({
                    key: "other_insurer_name",
                    type: "text",
                    label: t('Other'),
                    placeholder: t('Other')
                });
            }

            setFields(newFields.map((field) => ({ ...field, classname: "form-filed col-md-6", show: shouldShowField(field.key) })));
            return;
        }

        if (PREVIEW_TRIGGER_KEYS.includes(key)) schedulePreview(key);

        setRefresh(!refresh)
        return
    }

    const isAmountField = (key: string) => LEAD_DETAIL_AMOUNT_FIELDS?.includes(key);

    // Display only: read-only "Discount to Customer in Rp" is shown net of the Effective Customer Cashback.
    // The stored/submitted customer_discount value is not changed.
    const getDisplayValue = (field: any, disabled: boolean) => {
        const rawValue = getFieldItemValue(field);
        // a calculated (unsaved) quotation carries the pure discount; only the saved, combined value needs the cashback taken out
        if (field.key !== "customer_discount" || !disabled || showApproveBtn) return rawValue;
        const cashbackField = getFieldItem("offset_points_amt_after_tax");
        if (!cashbackField?.key) return rawValue;
        const cashback = toNumber(getFieldItemValue(cashbackField));
        if (!cashback) return rawValue;
        return toNumber(rawValue) - cashback;
    };

    /* ---------------- per-year (multi tenure) data ---------------- */
    const getInsuranceTypeLabel = (typeId: any, fallbackName?: string) =>
        INSURANCE_TYPE_SHORT_LABEL[+typeId] || fallbackName || commonState?.common?.insurance_type?.find((e: any) => +e.value === +typeId)?.label || '';

    const getYearSourceRows = (): any[] => {
        if (showApproveBtn && Array.isArray(quotationDetails?.addons_by_tenure) && quotationDetails.addons_by_tenure.length) {
            return quotationDetails.addons_by_tenure;
        }
        return leadDetail?.lead_tenure_data || [];
    };

    const buildYearRows = () => {
        const source = getYearSourceRows();
        const carField = getFieldItem('car_fmv');
        const accField = getFieldItem('acc_price');
        const baseCar = toNumber(carField?.key ? getFieldItemValue(carField) : leadDetail?.car_fmv);
        const baseAcc = isAccessoriesYes() ? toNumber(accField?.key ? getFieldItemValue(accField) : leadDetail?.acc_price) : 0;
        const savedTotal = toNumber(leadDetail?.total_fmv) || (baseCar + baseAcc);
        const addonRowsAll: any[] = leadDetail?.addon_detail_all || [];

        return Array.from({ length: currentTenure }, (_, idx) => {
            const year = idx + 1;
            const src = source.find((row: any) => +row.tenure === year) || {};
            const typeId = yearTypes?.[idx] ?? src.insurance_type_id ?? src.insurance_type;
            let depRate: number | null = hasValue(src.dep_rate) ? +src.dep_rate : null;
            if (depRate === null && hasValue(src.total_fmv) && savedTotal) depRate = (+src.total_fmv / savedTotal) * 100;
            if (depRate === null && year === 1) depRate = 100;

            const scale = (amount: number) => (year === 1 ? amount : (depRate === null ? null : Math.round(amount * depRate / 100)));
            const basic = hasValue(src.basic_premium_amount) ? toNumber(src.basic_premium_amount) : null;
            const addons = hasValue(src.total_addon_amount)
                ? toNumber(src.total_addon_amount)
                : ((hasValue(src.basic_addon_amount) || hasValue(src.special_addon_amount)) ? toNumber(src.basic_addon_amount) + toNumber(src.special_addon_amount) : null);

            const pricedAddons: any[] = Array.isArray(src.addon_list) ? src.addon_list : addonRowsAll.filter((a: any) => +a.tenure === year);
            const selectedIds: number[] = (isEdit && yearAddonSelections?.[year])
                ? yearAddonSelections[year]
                : pricedAddons.map((a: any) => +a.id);
            const addonsAmount = selectedIds.reduce((sum: number, id: number) => sum + toNumber(pricedAddons.find((a: any) => +a.id === +id)?.premium_amount), 0);

            // Non-Binder: the per-year TSI / accessories saved in Policy Terms Finalized win over the lead-level price scaled by the depreciation rate
            const savedTsi = hasValue(src.estimated_tsi) ? toNumber(src.estimated_tsi) : null;
            const savedAcc = hasValue(src.accessories_price) ? toNumber(src.accessories_price) : null;
            const yearTsi = savedTsi ?? scale(baseCar);
            const yearAcc = savedAcc ?? scale(baseAcc);

            return {
                year,
                typeLabel: getInsuranceTypeLabel(typeId, src.insurance_type_name),
                tsi: yearTsi,
                accPrice: yearAcc,
                totalFmv: (savedTsi !== null || savedAcc !== null) ? (yearTsi ?? 0) + (yearAcc ?? 0) : scale(baseCar + baseAcc),
                basic,
                addons,
                premium: basic !== null && addons !== null ? basic + addons : null,
                addonCount: (isEdit && year === 1) ? (addonCount ?? selectedIds.length) : selectedIds.length,
                addonsAmount,
            };
        });
    };

    /* ---------------- renderers ---------------- */
    const renderReadOnly = (key: string, label: string, amount: any, pending?: boolean) => (
        <fieldset className={`form-filed col-md-6 ${pending ? 'calc-pending' : ''}`} key={key}>
            <div className="material">
                <InputField
                    inputProps={{
                        id: key,
                        name: key,
                        className: "form-input",
                        type: "text",
                        value: amount === null || amount === undefined ? '-' : HELPER.addCurrencyFormatting(amount),
                        disabled: true,
                        spellCheck: false
                    }}
                />
                <label data-label={label} className="form-label"></label>
            </div>
        </fieldset>
    );

    const renderFieldComponent = (field: any, index?: number) => {
        const { key, type, classname, maxLength, placeholder, label } = field;
        const disabled = isFieldDisabled(key);
        const amountField = isAmountField(key);
        const value = getFieldItemValue(field);

        switch (type) {
            case "text":
                return (
                    <fieldset className={classname} key={key}>
                        <div className="material">
                            <InputField
                                inputProps={{
                                    id: key,
                                    name: key,
                                    className: "form-input",
                                    type,
                                    value: amountField
                                        ? HELPER.addCurrencyFormatting(getDisplayValue(field, disabled))
                                        // server % (up to 6 dp) is shown at 4 dp until the user edits it
                                        : (PERCENT_FIELDS.includes(key) && !field.edited)
                                            ? formatPercentValue(getFieldItemValue(field))
                                            : getFieldItemValue(field),
                                    maxLength,
                                    disabled,
                                    spellCheck:false
                                }}
                                onChange={(e: any) => handleChange(field, e)}
                            />
                            <label data-label={placeholder} className="form-label"></label>
                        </div>
                    </fieldset>
                );

            case "dd":
                if (key === "insurance_type_id") {
                    return (
                        <fieldset className={classname} key={key}>
                            <div className="material">
                                <InputField
                                    inputProps={{
                                        id: key,
                                        name: key,
                                        className: "form-input",
                                        type: "text",
                                        value: value,
                                        disabled: true,
                                        spellCheck: false,
                                    }}
                                />
                                <label data-label={placeholder} className="form-label"></label>
                            </div>
                        </fieldset>
                    );
                }

                return (
                    <React.Fragment key={key}>
                        <ReactSelect
                            options={getFieldItemOptions(field)}
                            value={value}
                            placeholder={placeholder}
                            handleChange={(e: any) => handleChange(field, e)}
                            fieldKey={key}
                            fieldSetClassName={classname}
                            isDisabled={disabled}
                        />
                    </React.Fragment>
                );

            case "date":
                return (
                    <fieldset
                        className={`form-filed date-picker-detail ${classname || ""}`}
                        key={index || key}
                    >
                        <div
                            className={`material animation-effect ${value ? "active-label" : ""
                                }`}
                        >
                            <ExpiryEffectivityDate
                                onChange={(e: Date) => handleChange(field, e)}
                                placeholder=""
                                text={label || placeholder}
                                insurancePolicyDate={new Date(value)}
                                flag={key}
                                showTimeSelect={false}
                                dateFormat="dd/MM/yyyy"
                                disabled={disabled}
                                minDate={moment().add(1, "day").toDate()} 
                                openToDate={new Date()} 
                            />
                            {field?.error_message && (
                                <span className="error-txt">{field.error_message}</span>
                            )}
                        </div>
                    </fieldset>
                );

            default:
                return null;
        }
    };

    const renderByKeys = (keys: string[]) => keys.map((key) => {
        if (key === "__tenure__") return renderTenureField();
        const field = fields.find((f: any) => f.key === key);
        return field && field.show ? renderFieldComponent(field) : null;
    });

    const getTenureOptions = () => {
        const list = commonState?.common?.tenure_year;
        if (Array.isArray(list) && list.length) return list.map((o: any) => ({ value: +o.value, label: o.label }));
        return Array.from({ length: MAX_TENURE }, (_, i) => ({ value: i + 1, label: `${i + 1} ${i ? t('Years', 'Years') : t('Year', 'Year')}` }));
    };

    const renderTenureField = () => {
        const options = getTenureOptions();
        return (
            <React.Fragment key="__tenure__">
                <ReactSelect
                    options={options}
                    value={options.find((o: any) => +o.value === +currentTenure) || null}
                    placeholder={t('Tenure', 'Tenure')}
                    handleChange={(e: any) => {
                        if (typeof onTenureChange === 'function') {
                            onTenureChange(+e?.value);
                            setActiveYear(1);
                        }
                    }}
                    fieldKey="tenure"
                    fieldSetClassName="form-filed col-md-6"
                    isDisabled={!isEdit || isCalculateView}
                />
            </React.Fragment>
        );
    };

    const openAddons = (year: number) => {
        showHideAddons(true, fields?.map((e: any) => ({ [e.key]: e.value }))?.reduce((acc: any, item: any) => ({ ...acc, ...item }), {}), year);
    };

    const renderAddonBar = (count: number, amount: number | null, year?: number) => (
        <fieldset className="form-filed col-md-6" key={`addon-bar-${year || 0}`}>
            <div className="addons addons-filed">
                <div className="addonwrapper">
                    <label className="selectedWrapper">
                        {count} {t('Add Ons', 'Add Ons')}
                        {isMultiYear && amount !== null ? <span className="addon-bar-amount">{HELPER.addCurrencyFormatting(amount)}</span> : null}
                    </label>
                    <button className="btn-line btn-addon" disabled={!isEdit || isCalculateView} onClick={() => openAddons(year || 1)}>
                        {t('Select Add Ons')}<i className="ic-keyboard_arrow_right"></i>
                    </button>
                </div>
            </div>
        </fieldset>
    );

    const renderYearFields = (row: any) => {
        const pending = isEdit && !!needsRecalc;
        const accField = fields.find((f: any) => f.key === 'acc_price');
        const tsiField = fields.find((f: any) => f.key === 'car_fmv');
        return (
            <>
                {row.year === 1 && tsiField
                    ? renderFieldComponent(tsiField)
                    : renderReadOnly(`tsi_${row.year}`, `${t('Estimated TSI')} *`, row.tsi)}
                {row.year === 1 && accField
                    ? renderFieldComponent(accField)
                    : renderReadOnly(`acc_price_${row.year}`, t('Price of Accessories'), row.accPrice)}
                {renderReadOnly(`total_fmv_${row.year}`, t('Overall Car Price'), row.totalFmv)}
                {renderReadOnly(`basic_premium_${row.year}`, t('Basic Premium'), row.basic, pending)}
                {renderReadOnly(`total_addons_${row.year}`, t('Total Addons Premium'), row.addons, pending)}
                {renderReadOnly(`premium_year_${row.year}`, t('Premium (This Year)', 'Premium (This Year)'), row.premium, pending)}
                {renderAddonBar(row.addonCount, row.addonsAmount, row.year)}
            </>
        );
    };

    const renderPremiumSection = () => {
        if (!isMultiYear) {
            return (
                <>
                    <div className="col-md-12"><h4 className="calc-section-title">{t('Premium & TSI Details', 'Premium & TSI Details')}</h4></div>
                    {renderByKeys(PREMIUM_KEYS)}
                    {isEdit && renderAddonBar(addonCount, null)}
                </>
            );
        }
        const rows = buildYearRows();
        const active = rows.find((r: any) => r.year === activeYear) || rows[0];
        return (
            <>
                <div className="col-md-12"><h4 className="calc-section-title">{t('Year-wise Premium & TSI Details', 'Year-wise Premium & TSI Details')}</h4></div>
                <div className="col-md-12">
                    <ul className="calc-year-tabs">
                        {rows.map((row: any) => (
                            <li key={row.year} className={`calc-year-tab ${row.year === active.year ? 'active' : ''}`} onClick={() => setActiveYear(row.year)}>
                                <span className="calc-year-label">{t('Year', 'Year')} {row.year}</span>
                                {row.typeLabel ? <span className="calc-year-tag">{row.typeLabel}</span> : null}
                            </li>
                        ))}
                    </ul>
                </div>
                {isEdit && needsRecalc ? (
                    <div className="col-md-12">
                        <div className="calc-recalc-hint">{t('Add-on or tenure selection changed — click Calculate to refresh premiums.', 'Add-on or tenure selection changed — click Calculate to refresh premiums.')}</div>
                    </div>
                ) : null}
                {renderYearFields(active)}
            </>
        );
    };

    // Rp and % are shown in sync, but the API gives Rp precedence over % - send only the one the user typed
    // so a typed % isn't re-derived from its rounded Rp value
    const buildCalculateFields = () => {
        const edited = lastDiscountKey.current;
        if (!edited) return fields;
        const derived = edited === 'customer_discount' ? 'customer_discount_per' : 'customer_discount';
        return fields.map((f: any) => (f.key === derived ? { ...f, value: 0 } : f));
    };

    // discount / cashback typed -> agent commission, agent points, cashback and total premium follow (500 ms debounce)
    const getCashbackTaxPercent = () => {
        const holder = +(insuranceState?.policy_holder ?? leadDetail?.policy_holder);
        return holder === POLICY_HOLDER_PERSONAL ? PERSONAL_OFFSET_POINTS_TAX_PERCENT
            : holder === POLICY_HOLDER_COMPANY ? COMPANY_OFFSET_POINTS_TAX_PERCENT : 0;
    };
    const getMaxCashbackPer = () => basePointsRef.current
        ?? (Number(leadDetail?.point_commision || 0) + Number(leadDetail?.offset_points || 0));
    // gross cashback % (of the whole-policy GWP) that a typed value in any of the 3 cashback inputs stands for
    const getCashbackPercent = (key: string, value: any) => {
        if (key === 'offset_points') return Number(value) || 0;
        const gwp = toNumber(getFieldItemValue(getFieldItem('gwp_amount')));
        if (!gwp) return 0;
        let amount = toNumber(value);
        if (key === 'offset_points_amt_after_tax') {
            const tax = getCashbackTaxPercent();
            amount = tax < 100 ? amount / (1 - tax / 100) : 0;
        }
        return (amount / gwp) * 100;
    };

    const PREVIEW_TRIGGER_KEYS = ["customer_discount", "customer_discount_per", "offset_points", "offset_points_amt", "offset_points_amt_after_tax", "cash_customer_per", "cash_customer"];
    const PREVIEW_RESULT_KEYS = ["gwp_amount", "agent_commision", "agent_comission_amount", "point_commision", "agent_point_amount", "offset_points", "offset_points_amt", "offset_points_amt_after_tax", "cash_customer_per", "cash_customer", "final_premium"];
    const schedulePreview = (typedKey: string) => {
        if (!previewInsurance || !isEdit) return;
        clearTimeout(previewTimer.current);
        const requestId = ++previewRequestId.current;
        setIsPreviewing(true);
        previewTimer.current = setTimeout(async () => {
            const quotation = await previewInsurance(buildCalculateFields(), { cashback: buildCashbackParams() });
            if (requestId !== previewRequestId.current) return;
            setIsPreviewing(false);
            if (!quotation) return;
            if (!isCrmLead && +quotation.point_commision > 0) {
                basePointsRef.current = +quotation.point_commision + (+quotation.offset_points || 0);
            }
            // the derived side of the Rp / % discount pair follows the GWP the calculator just returned
            const gwp = toNumber(quotation.gwp_amount);
            const discountKey = lastDiscountKey.current;
            const discountTyped = discountKey ? toNumber(getFieldItem(discountKey)?.value) : 0;
            const derivedDiscount: Record<string, any> = !discountKey || !gwp ? {}
                : discountKey === 'customer_discount_per'
                    ? { customer_discount: Math.round((gwp * discountTyped) / 100) }
                    : { customer_discount_per: +((discountTyped / gwp) * 100).toFixed(PERCENT_INPUT_DECIMALS) };
            setFields((prev: any[]) => prev.map((f: any) => {
                if (f.key === typedKey) return f;
                if (derivedDiscount.hasOwnProperty(f.key)) return { ...f, value: derivedDiscount[f.key], edited: false };
                if (!PREVIEW_RESULT_KEYS.includes(f.key) || !hasValue(quotation[f.key])) return f;
                const value = PERCENT_FIELDS.includes(f.key) ? formatPercentValue(quotation[f.key]) : quotation[f.key];
                return { ...f, value, edited: false };
            }));
        }, 500);
    };

    // CRM cashback -> cash_customer (whole-policy Rp) / cash_customer_per; the API spreads the Rp across the policy years
    const buildCrmCashbackParams = () => {
        if (!isCashbackUnlocked()) return { cash_customer: 0, cash_customer_per: 0 };
        const key = lastCashbackKey.current;
        if (!key) {
            // not edited: keep the saved cashback so a recalculation doesn't silently drop it
            if (+leadDetail?.cash_customer) return { cash_customer: Number(leadDetail.cash_customer), cash_customer_per: 0 };
            return +leadDetail?.cash_customer_per ? { cash_customer_per: Number(leadDetail.cash_customer_per), cash_customer: 0 } : {};
        }
        const raw = getFieldItem(key)?.value;
        if (key === 'cash_customer_per') return { cash_customer_per: Number(raw || 0), cash_customer: 0 };
        if (key === 'cash_customer') return { cash_customer: toNumber(raw), cash_customer_per: 0 };
        return {};
    };

    const buildCashbackParams = () => {
        if (isCrmLead) return buildCrmCashbackParams();
        if (!isCashbackUnlocked()) return { offset_points: 0, offset_points_amount: 0 };
        const key = lastCashbackKey.current;
        if (!key) {
            // not edited: keep the saved whole-policy cashback so a recalculation doesn't silently drop it
            // (CRM leads are handled by buildCrmCashbackParams)
            if (+leadDetail?.offset_points_type === 2 && +leadDetail?.offset_points_amt) return { offset_points_amount: Number(leadDetail.offset_points_amt), offset_points: 0 };
            return +leadDetail?.offset_points ? { offset_points: Number(leadDetail.offset_points), offset_points_amount: 0 } : {};
        }
        const raw = getFieldItem(key)?.value;
        const value = PERCENT_KEYS.includes(key) ? Number(raw || 0) : toNumber(raw);
        switch (key) {
            case 'offset_points': return { offset_points: value, offset_points_amount: 0 };
            case 'offset_points_amt': return { offset_points_amount: value, offset_points: 0 };
            case 'offset_points_amt_after_tax': return { effective_point_amount: value, offset_points: 0, offset_points_amount: 0 };
            default: return {};
        }
    };
    
    useEffect(() => {        
        const allFields = [
            { key: "insurer_name", type: "text", label: t('Insurance Name'), placeholder: t('Insurance Name') },
            { key: "insurance_type_id", type: "dd", label: t('Insurance Type'), placeholder: t('Insurance Type') },
            { key: "vehicle_condition_id", type: "dd", label: t('Condition'), placeholder: t('Condition') },
            { key: "usage_type", type: "dd", label: t('Usage Type'), placeholder: t('Usage Type') },
            { key: "insurance_starting_date", type: "text", label: t('Period'), placeholder: t('Period') },
            { key: "make", type: "dd", label: t('Make'), placeholder: t('Make') },
            { key: "model", type: "dd", label: t('Model'), placeholder: t('Model') },
            { key: "version", type: "dd", label: t('Version'), placeholder: t('Version') },
            { key: "make_year", type: "dd", label: t('Make Year'), placeholder: t('Make Year') },
            { key: "plate_area", type: "dd", label: t('Plate Area'), placeholder: t('Plate Area') },
            { key: "plate_number_region", type: "text", label: t('Plate Region'), placeholder: t('Plate Region') },
            { key: "car_fmv", type: "text", label: t('Estimated TSI'), placeholder: `${t('Estimated TSI')} *` },
            { key: "is_accessories", type: "dd", label: t('Accessories'), placeholder: t('Accessories') },
            { key: "acc_count", type: "dd", label: t('No Of Accesories'), placeholder: t('No Of Accesories') },
            { key: "accessories_description", type: "text", label: t('Items'), placeholder: t('Items'), maxLength: 250 },
            { key: "acc_price", type: "text", label: t('Price of Accessories'), placeholder: t('Price of Accessories') },
            { key: "total_fmv", type: "text", label: t('Overall Car Price'), placeholder: t('Overall Car Price') },
            { key: "basic_premium_amount", type: "text", label: t('Basic Premium'), placeholder: t('Basic Premium') },
            { key: "admin_fee", type: "text", label: t('Admin Fee'), placeholder: t('Admin Fee') },
            { key: "customer_discount", type: "text", label: t('Discount to Customer in Rp'), placeholder: `${t('Discount to Customer in Rp')} *` },
            { key: "customer_discount_per", type: "text", label: `${t('Discount to Customer in')} %`, placeholder: `${t('Discount to Customer in')} % *` },
            { key: "final_premium", type: "text", label: t('Total Premium Amount', 'Total Premium Amount'), placeholder: t('Total Premium Amount', 'Total Premium Amount') },
            { key: "agent_commision", type: "text", label: t('Agent Commission %'), placeholder: t('Agent Commission %') },
            { key: "agent_comission_amount", type: "text", label: t('Agent Commission'), placeholder: t('Agent Commission') },
            { key: "point_commision", type: "text", label: t('Agent Points %'), placeholder: t('Agent Points %') },
            { key: "agent_point_amount", type: "text", label: t('Agent Points'), placeholder: t('Agent Points') },
            { key: "total_addons_premium", type: "text", label: t('Total Addons Premium'), placeholder: t('Total Addons Premium') },
            { key: "gwp_amount", type: "text", label: t('Gross Written Premium'), placeholder: t('Gross Written Premium') },
            { key: "copy_of_insurance", type: "dd", label: t('Required Physical Copy of Insurance'), placeholder: t('Required Physical Copy of Insurance') },
            { key: "insurance_exists", type: "dd", label: t('Insurance Policy Exists'), placeholder: t('Insurance Policy Exists') },
            { key: "existing_insurer_name", type: "dd", label: t('Insurance Policy Insurer'), placeholder: t('Insurance Policy Insurer') },
            { key: "other_insurer_name", type: "text", label: t('Other'), placeholder: t('Other') },
            { key: "insurance_expire_date", type: "date", label: t('Insurance Policy Expiration Date'), placeholder: t('Insurance Policy Expiration Date') },
            { key: "rule_engine_id", type: "dd", label: `${t('Application Type')} *`, placeholder: t('Application Type') },
            { key: "bstk_insurer_date", type: "date", label: t('BSTK Date'), placeholder: t('BSTK Insurer Date') },
        ]

        // CRM: cashback is cash_customer_per / cash_customer (no Effective Cashback); others: offset_points*
        if (insuranceState?.leadDetail?.lead_type === LEAD_TYPE_CRM) allFields.push(
            { key: "cash_customer_per", type: "text", label: `${translations.Insurance?.['Customer Cashback (%)'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Customer Cashback (%)'] || translations.commonMsg?.loading}` },
            { key: "cash_customer", type: "text", label: `${translations.Insurance?.['Customer Cashback (Amount)'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Customer Cashback (Amount)'] || translations.commonMsg?.loading}` }
        );
        else allFields.push(
            { key: "offset_points", type: "text", label: `${translations.Insurance?.['Customer Cashback (%)'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Customer Cashback (%)'] || translations.commonMsg?.loading}` },
            { key: "offset_points_amt", type: "text", label: `${translations.Insurance?.['Customer Cashback (Amount)'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Customer Cashback (Amount)'] || translations.commonMsg?.loading}` },
            { key: "offset_points_amt_after_tax", type: "text", label: `${translations.Insurance?.['Effective Customer Cashback (Amount)'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Effective Customer Cashback (Amount)'] || translations.commonMsg?.loading}` }
        );

        if (insuranceState?.leadDetail?.lead_type === LEAD_TYPE_CRM) {
            allFields.push(
                { key: "agent_commision", type: "text", label: `${translations.Insurance?.['Agent Commission %'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Agent Commission %'] || translations.commonMsg?.loading}` },
                { key: "point_commision", type: "text", label: `${translations.Insurance?.['Agent Points %'] || translations.commonMsg?.loading}`, placeholder: `${translations.Insurance?.['Agent Points %'] || translations.commonMsg?.loading}` }
            );
        }
        
        // Get the current value of insurance_exists
        const insuranceExistsField = fields?.find((f: any) => f.key === 'insurance_exists');        
        const insuranceExistsValue = insuranceExistsField?.value?.value ?? leadDetail?.insurance_exists;
        const existsInQuotation = quotationDetails?.insurance_exists?.value === 1 || quotationDetails?.insurance_exists?.value === '1';

        // Get the current value of existing_insurer_name
        const existingInsurerField = fields?.find((f: any) => f.key === 'existing_insurer_name');
        const existingInsurerValue = existingInsurerField?.value?.value ?? leadDetail?.existing_insurer_name;   
        const otherInQuotation = quotationDetails?.existing_insurer_name?.value === 0 || quotationDetails?.existing_insurer_name?.value === '0';
        
        const filteredFields = allFields.filter((field) => {
            if (field.key === 'existing_insurer_name' || field.key === 'insurance_expire_date') {
                return (!showApproveBtn && insuranceExistsValue === 1) || (!showApproveBtn && insuranceExistsValue === '1') || existsInQuotation;
            }

            if (field.key === 'other_insurer_name') {
                return ((insuranceExistsValue == 1) && (existingInsurerValue == 0) && !showApproveBtn) || otherInQuotation;
            }

            if (field.key === 'bstk_insurer_date') {
                return leadDetail?.vehicle_condition_id === VEHICLE_CONDITION['NEW'];
            }
            return true;
        });

        const formFields = filteredFields.map((field) => ({
            ...field,
            classname: "form-filed col-md-6",
            show: shouldShowField(field.key),
        }));

        setFields(formFields);
        lastCashbackKey.current = null;
        lastDiscountKey.current = null;
        basePointsRef.current = null;
        previewRequestId.current += 1;
        clearTimeout(previewTimer.current);
        setIsPreviewing(false);
        dispatch(setQuotationAddonsData(null));
        // eslint-disable-next-line
    }, [translations, totalAddonPremiumAmt, insuranceState?.leadDetail, quotationDetails, showApproveBtn]); // Dependencies added

    // resolve every field value up-front: some are not rendered in this layout (vehicle fields, Year-1 values while
    // another year tab is open) but Calculate and the share popup still read them from the field list
    fields.forEach((field: any) => getFieldItemValue(field));

    return (
        <div className="loan-form-filed-scroll">
            <div className="loan-filed-sec">
                <div className="form-group-bx">
                    <div className="row align-center">
                        <div className="col-md-6">
                            <h3>{t('Insurance Policy Loan Quotation')}</h3>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12"><div className="calc-subsection-label">{t('Coverage Configuration', 'Coverage Configuration')}</div></div>
                        {renderByKeys(COVERAGE_KEYS)}
                    </div>
                    <hr className="calc-subsection-divider" />

                    <div className="row">
                        <div className="col-md-12"><div className="calc-subsection-label">{t('Policy Details', 'Policy Details')}</div></div>
                        {renderByKeys(POLICY_KEYS)}
                    </div>

                    <div className="row">
                        {renderPremiumSection()}
                    </div>
                    <hr className="calc-subsection-divider" />

                    <div className="row">
                        <div className="col-md-12"><h4 className="calc-section-title">{t('Commercial Terms (Whole Policy)', 'Commercial Terms (Whole Policy)')}</h4></div>
                        {renderByKeys(isCrmLead ? COMMERCIAL_KEYS_CRM : COMMERCIAL_KEYS_NON_CRM)}
                    </div>
                </div>

                {isEdit && (
                    <div className="btn-save-remarks">
                        <button className="btn-primary" disabled={isPreviewing} onClick={() => {
                            setIsCalculateView(true);
                            // re-enable the form if the calculation is rejected (validation / no quotation)
                            Promise.resolve(calculateInsurance(buildCalculateFields(), { cashback: buildCashbackParams() })).finally(() => setIsCalculateView(false));
                        }}>{translations.commonMsg?.['Calculate'] || translations.commonMsg?.loading}</button>

                        <button className="btn-line" onClick={() => {
                            toggleswitch("SUMMARY");
                            setIsCalculateView(false);
                        }}>{translations.commonMsg?.['cancel'] || translations.commonMsg?.loading}</button>
                    </div>
                )}

                {showApproveBtn && (
                    <div className="show text-al-r">
                        <button className="btn-primary" onClick={saveInsurance}>{translations.commonMsg?.['Approve'] || translations.commonMsg?.loading}</button>
                    </div>
                )}
            </div>
        </div >
    );
}
